import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const COLLAPSED_LINE_COUNT = 2;
const COLLAPSED_HEIGHT_CLASS = 'max-h-[2lh]';
const TOGGLE_BUTTON_CLASS =
  'inline-flex items-center gap-1 rounded-sm text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40';
const HEIGHT_TRANSITION_CLASS =
  'overflow-hidden transition-[max-height] duration-300 ease-in-out motion-reduce:transition-none';

interface ExpandableCommentProps {
  comment: string;
  shouldKeepLineBreaks?: boolean | undefined;
  className?: string | undefined;
}

function getCollapsedHeight(commentElement: HTMLElement): number {
  const lineHeight = Number.parseFloat(getComputedStyle(commentElement).lineHeight);
  if (Number.isNaN(lineHeight)) return commentElement.clientHeight;
  return Math.ceil(lineHeight * COLLAPSED_LINE_COUNT);
}

export function ExpandableComment({
  comment,
  shouldKeepLineBreaks = false,
  className,
}: ExpandableCommentProps) {
  const commentRef = useRef<HTMLSpanElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);

  useLayoutEffect(() => {
    const commentElement = commentRef.current;
    if (!commentElement) return;
    const observer = new ResizeObserver(() => {
      setFullHeight(commentElement.scrollHeight);
      setCollapsedHeight(getCollapsedHeight(commentElement));
    });
    observer.observe(commentElement);
    return () => observer.disconnect();
  }, [comment]);

  const isClamped = fullHeight > collapsedHeight;

  return (
    <span className="flex flex-col items-start gap-0.5">
      <span
        ref={commentRef}
        style={isExpanded ? { maxHeight: fullHeight } : undefined}
        className={cn(
          'block wrap-break-word',
          shouldKeepLineBreaks && 'whitespace-pre-line',
          HEIGHT_TRANSITION_CLASS,
          !isExpanded && COLLAPSED_HEIGHT_CLASS,
          className,
        )}
      >
        {comment}
      </span>
      {isClamped && (
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          aria-expanded={isExpanded}
          className={TOGGLE_BUTTON_CLASS}
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <ChevronDown
            className={cn(
              'size-3.5 transition-transform duration-300 motion-reduce:transition-none',
              isExpanded && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      )}
    </span>
  );
}
